import { ref, reactive } from 'vue';
import { httpService } from '../services/httpService';
import { log, statusTrace } from '../utils/DebugLogger';
import { useDateFormatter } from './useDateFormatter';
import { validate } from '../contracts/validate';
import '../contracts/Payroll_CreatePeriodInput.ts';
import '../contracts/Payroll_FranchiseIdInput.ts';
import '../contracts/Payroll_ArchivePeriodResponse';
import '../contracts/Payroll_NextPeriodDatesResponse';
import '../contracts/Payroll_PayPeriodResponse';
import '../contracts/Payroll_PeriodResponse';
import '../contracts/Payroll_PeriodSummaryResponse';
import '../contracts/Payroll_ReversePeriodResponse';
import '../contracts/Payroll_UndoPeriodPreviewResponse';
import '../contracts/Payroll_UndoPeriodResponse';
import '../contracts/Payroll_CreatePeriodResponse';
import '../contracts/Payroll_PeriodsResponse';
import type { Payroll_FranchiseIdInput } from '../contracts/Payroll_FranchiseIdInput';
import {
  PAYROLL_STATUS,
  PAYROLL_STATUS_LABELS,
  PERIOD_STATUS,        // Backward compatibility alias
  PERIOD_STATUS_LABELS, // Backward compatibility alias
  isStatusAtLeast as checkStatusAtLeast,
  getStatusLabel,
  getWizardState
} from './usePayrollStatus';

// Re-export from usePayrollStatus for backward compatibility
// Components importing from usePayrollPeriods will still work
export { PERIOD_STATUS, PERIOD_STATUS_LABELS };

// Re-export new centralized constants and functions
export { PAYROLL_STATUS, PAYROLL_STATUS_LABELS, getStatusLabel, getWizardState };

log('usePayrollPeriods', 'module', 'Loaded with centralized status from usePayrollStatus.js');

/**
 * TYPED DATA LAYER: Date handling follows the typed data layer pattern:
 * - DATE-ONLY fields: Use fromDateToApiDate (YYYY-MM-DD, no timezone conversion)
 * - DATETIME fields: Use toApiDateTimeFormat/fromApiDateTimeFormat (ISO 8601 UTC)
 * Date-only fields represent calendar dates, not moments in time.
 */

// =============================================================================
// SINGLETON STATUS STORE - Single source of truth for period statuses
// =============================================================================
// Module-level singleton ensures all composable instances share the same state
export const periodStatusStore = reactive(new Map());

/**
 * Update period status in the centralized store
 * IMPORTANT: This should ONLY be called with data from API responses
 *
 * @param {number} periodId - Period ID
 * @param {number} status - New status value from API response
 * @param {string} source - Source of the update (for logging)
 */
export const updatePeriodStatus = (periodId: any, status: any, source = 'api') => {
  const id = parseInt(periodId);
  const newStatus = parseInt(status);

  if (isNaN(id) || isNaN(newStatus)) {
    log('usePayrollPeriods', 'updatePeriodStatus', 'Invalid periodId or status', { periodId, status });
    statusTrace('usePayrollPeriods.updatePeriodStatus', 'INVALID_INPUT', { periodId, status, source });
    return;
  }

  const oldStatus = periodStatusStore.get(id);
  periodStatusStore.set(id, newStatus);

  // STATUS_TRACE: This is the CENTRAL point where status is updated in frontend store
  statusTrace('usePayrollPeriods.updatePeriodStatus', 'STATUS_CHANGED', {
    periodId: id,
    status: newStatus,
    oldStatus,
    source,
    label: PERIOD_STATUS_LABELS[newStatus] || 'Unknown'
  });

  log('usePayrollPeriods', 'updatePeriodStatus', 'Status updated', {
    periodId: id,
    oldStatus,
    newStatus,
    source,
    label: PERIOD_STATUS_LABELS[newStatus] || 'Unknown'
  });
};

/**
 * Get period status from the centralized store
 *
 * @param {number} periodId - Period ID
 * @returns {number|undefined} Current status or undefined if not in store
 */
export const getPeriodStatus = (periodId: any) => {
  const id = parseInt(periodId);
  const status = periodStatusStore.get(id);
  statusTrace('usePayrollPeriods.getPeriodStatus', 'READ_STATUS', { periodId: id, status });
  return status;
};

/**
 * Check if period has at least the specified status
 * Uses centralized checkStatusAtLeast from usePayrollStatus.js
 *
 * @param {number} periodId - Period ID
 * @param {number} minStatus - Minimum required status
 * @returns {boolean}
 */
export const isStatusAtLeast = (periodId: any, minStatus: any) => {
  const currentStatus = getPeriodStatus(periodId);
  if (currentStatus === undefined) {
    log('usePayrollPeriods', 'isStatusAtLeast', 'Status not found in store', { periodId, minStatus });
    return false;
  }
  const result = checkStatusAtLeast(currentStatus, minStatus);
  log('usePayrollPeriods', 'isStatusAtLeast', 'Checked status', { periodId, currentStatus, minStatus, result });
  return result;
};

/**
 * Remove period from status store (e.g., after deletion)
 *
 * @param {number} periodId - Period ID
 */
export const clearPeriodStatus = (periodId: any) => {
  const id = parseInt(periodId);
  periodStatusStore.delete(id);
  log('usePayrollPeriods', 'clearPeriodStatus', 'Status cleared', { periodId: id });
};

/**
 * Helper to extract and update status from period data
 *
 * @param {Object} periodData - Period object from API response
 */
export const syncStatusFromPeriodData = (periodData: any) => {
  if (!periodData) return;

  const periodId = periodData.id || periodData.period_id;
  const status = periodData.status;

  statusTrace('usePayrollPeriods.syncStatusFromPeriodData', 'SYNC_FROM_PERIOD_DATA', {
    periodId,
    status,
    hasData: !!periodData
  });

  if (periodId !== undefined && status !== undefined) {
    updatePeriodStatus(periodId, status, 'period_data');
  }
};

/**
 * Composable for payroll periods management
 */
export function usePayrollPeriods() {
  const periods = ref([] as any[]);
  const currentPeriod = ref(null as any);
  const isLoading = ref(false);
  const error = ref(null);

  // Initialize date formatter for typed data layer conversions
  // DATE-ONLY: fromDateToApiDate (no timezone conversion)
  // DATETIME: toApiDateTimeFormat/fromApiDateTimeFormat (UTC conversion)
  const {
    fromDateToApiDate
  } = useDateFormatter();

  /**
   * List payroll periods
   * Backend uses filtro_model to get selected franchises
   * @param {string} status - Optional status filter (draft, calculated, paid, archived, reversed)
   * @returns {Promise<Array>}
   */
  const listPeriods = async (status = null) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollPeriods', 'listPeriods', 'Listing periods from filter', { status });

    try {
      const params: any = {};
      if (status) params.status = status;

      const response = await httpService.get('/payroll/periods', { params,
        responseContract: 'Payroll_PeriodsResponse' });

      if (!response.success) {
        throw new Error(response.error || 'Failed to list periods');
      }

      periods.value = response.dados?.periods || [];

      // Sync status for all loaded periods
      periods.value.forEach(period => syncStatusFromPeriodData(period));

      log('usePayrollPeriods', 'listPeriods', 'Periods loaded', { count: periods.value.length });

      return periods.value;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollPeriods', 'listPeriods', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get period details
   * @param {number} periodId
   * @param {number} franchiseId
   * @returns {Promise<Object>}
   */
  const getPeriod = async (periodId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollPeriods', 'getPeriod', 'Fetching period', { periodId, franchiseId });

    try {
      const response = await httpService.get(`/payroll/period/${periodId}`, {
        params: { franchise_id: franchiseId },
        responseContract: 'Payroll_PeriodResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to get period');
      }

      currentPeriod.value = response.dados;

      // Sync status from API response
      syncStatusFromPeriodData(currentPeriod.value);

      log('usePayrollPeriods', 'getPeriod', 'Period loaded', { periodId: currentPeriod.value?.id });

      return currentPeriod.value;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollPeriods', 'getPeriod', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Create new payroll period
   * @param {number} franchiseId
   * @param {string} frequency - weekly, biweekly, monthly
   * @param {Date|string} startDate - Optional start date (Date object or YYYY-MM-DD string)
   * @param {Date|string} endDate - Optional end date (Date object or YYYY-MM-DD string)
   * @returns {Promise<Object>}
   */
  const createPeriod = async (franchiseId: number, frequency: string, startDate: string | null = null, endDate: string | null = null) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollPeriods', 'createPeriod', 'Creating period', {
      franchiseId,
      frequency,
      startDate,
      endDate
    });

    try {
      const data: any = {
        franchise_id: franchiseId,
        frequency
      };

      // TYPED DATA LAYER: Date-only fields use fromDateToApiDate (no timezone conversion)
      if (startDate) {
        data.start_date = startDate;
      }
      if (endDate) {
        data.end_date = endDate;
      }

      validate('Payroll_CreatePeriodInput', data);
      const response = await httpService.post('/payroll/create_period', data, { responseContract: 'Payroll_CreatePeriodResponse', inputContract: 'Payroll_CreatePeriodInput' });

      if (!response.success) {
        throw new Error(response.error || 'Failed to create period');
      }

      currentPeriod.value = response.dados;

      // Sync status from API response (new period starts at OPEN)
      syncStatusFromPeriodData(currentPeriod.value);

      log('usePayrollPeriods', 'createPeriod', 'Period created', { periodId: currentPeriod.value?.id });

      return currentPeriod.value;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollPeriods', 'createPeriod', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Archive a paid period
   * @param {number} periodId
   * @param {number} franchiseId
   * @returns {Promise<boolean>}
   */
  const archivePeriod = async (periodId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollPeriods', 'archivePeriod', 'Archiving period', { periodId, franchiseId });

    try {
      const payload: Payroll_FranchiseIdInput = { franchise_id: franchiseId };
      validate('Payroll_FranchiseIdInput', payload);
      const response = await httpService.post(`/payroll/archive_period/${periodId}`, payload, {
        responseContract: 'Payroll_ArchivePeriodResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to archive period');
      }

      // Archive endpoint returns a boolean success flag — status is implicit ARCHIVED
      updatePeriodStatus(periodId, PERIOD_STATUS.ARCHIVED, 'archive_period');

      log('usePayrollPeriods', 'archivePeriod', 'Period archived', { periodId });
      return true;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollPeriods', 'archivePeriod', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete a draft period
   * @param {number} periodId
   * @returns {Promise<boolean>}
   */
  const deletePeriod = async (periodId: number) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollPeriods', 'deletePeriod', 'Deleting period', { periodId });

    try {
      const response = await httpService.delete(`/payroll/periods/${periodId}`, { responseContract: 'Payroll_PeriodsResponse' });

      if (!response.success) {
        throw new Error(response.error || 'Failed to delete period');
      }

      // Clear status from store since period no longer exists
      clearPeriodStatus(periodId);

      log('usePayrollPeriods', 'deletePeriod', 'Period deleted', { periodId });
      return true;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollPeriods', 'deletePeriod', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Mark a calculated period as paid
   * @param {number} periodId
   * @param {number} franchiseId
   * @returns {Promise<boolean>}
   */
  const payPeriod = async (periodId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollPeriods', 'payPeriod', 'Marking period as paid', { periodId, franchiseId });

    try {
      const payload: Payroll_FranchiseIdInput = { franchise_id: franchiseId };
      validate('Payroll_FranchiseIdInput', payload);
      const response = await httpService.post(`/payroll/pay_period/${periodId}`, payload, {
        responseContract: 'Payroll_PayPeriodResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to pay period');
      }

      // pay_period endpoint returns the list of created salary expenses; the period
      // status transitions to PAID implicitly.
      updatePeriodStatus(periodId, PERIOD_STATUS.PAID, 'pay_period');

      log('usePayrollPeriods', 'payPeriod', 'Period marked as paid', { periodId });
      return true;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollPeriods', 'payPeriod', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Reverse a paid period
   * @param {number} periodId
   * @param {number} franchiseId
   * @returns {Promise<boolean>}
   */
  const reversePeriod = async (periodId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollPeriods', 'reversePeriod', 'Reversing period', { periodId, franchiseId });

    try {
      const payload: Payroll_FranchiseIdInput = { franchise_id: franchiseId };
      validate('Payroll_FranchiseIdInput', payload);
      const response = await httpService.post(`/payroll/reverse_period/${periodId}`, payload, {
        responseContract: 'Payroll_ReversePeriodResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to reverse period');
      }

      // reverse_period endpoint returns a boolean success flag; reversed period
      // transitions back to CALCULATED.
      updatePeriodStatus(periodId, PERIOD_STATUS.CALCULATED, 'reverse_period');

      log('usePayrollPeriods', 'reversePeriod', 'Period reversed', { periodId });
      return true;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollPeriods', 'reversePeriod', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get undo preview for a payroll period
   * Shows what will be affected by the undo operation
   * @param {number} periodId
   * @param {number} franchiseId
   * @returns {Promise<Object>} - { can_undo, reason, affected: { executions, deductions, recebimentos, despesas } }
   */
  const getUndoPreview = async (periodId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollPeriods', 'getUndoPreview', 'Fetching undo preview', { periodId, franchiseId });

    try {
      const response = await httpService.get(`/payroll/undo_period_preview/${periodId}`, {
        params: { franchise_id: franchiseId },
        responseContract: 'Payroll_UndoPeriodPreviewResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to get undo preview');
      }
      if (!response.dados) {
        throw new Error(response.error || 'Empty response from undo preview');
      }

      // Envelope: { success, dados: { success, message, data } }. The nested
      // `data` is the actual preview row (or null when undo isn't possible).
      const preview = response.dados.data;
      log('usePayrollPeriods', 'getUndoPreview', 'Preview loaded', preview);

      return preview;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollPeriods', 'getUndoPreview', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Undo a payroll period (pre-confirmation only)
   * Completely removes the period and all associated data
   * @param {number} periodId
   * @param {number} franchiseId
   * @returns {Promise<Object>} - { success, summary }
   */
  const undoPeriod = async (periodId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollPeriods', 'undoPeriod', 'Undoing period', { periodId, franchiseId });

    try {
      const payload: Payroll_FranchiseIdInput = { franchise_id: franchiseId };
      validate('Payroll_FranchiseIdInput', payload);
      const response = await httpService.post(`/payroll/undo_period/${periodId}`, payload, {
        responseContract: 'Payroll_UndoPeriodResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to undo period');
      }

      // Clear status from store since period has been undone
      clearPeriodStatus(periodId);

      const summary = response.dados;
      log('usePayrollPeriods', 'undoPeriod', 'Period undone', summary);

      return summary;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollPeriods', 'undoPeriod', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get next period dates for a franchise and frequency
   * Calculates the next period based on the latest existing period
   * @param {number} franchiseId
   * @param {string} userId - Employee Cognito sub (user_id)
   * @returns {Promise<Object>} - { start, end, has_previous, previous_end_date }
   */
  const getNextPeriodDates = async (franchiseId: any, userId: any) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollPeriods', 'getNextPeriodDates', 'Fetching next period dates', { franchiseId, userId });

    try {
      const response = await httpService.get(`/payroll/next_period_dates/${franchiseId}/${userId}`, {
        responseContract: 'Payroll_NextPeriodDatesResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to get next period dates');
      }

      const dates = response.dados;
      log('usePayrollPeriods', 'getNextPeriodDates', 'Next period dates loaded', dates);

      return dates;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollPeriods', 'getNextPeriodDates', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Get period summary with calculations
   * @param {number} periodId
   * @param {number} franchiseId
   * @returns {Promise<Object>}
   */
  const getPeriodSummary = async (periodId: any, franchiseId: any) => {
    isLoading.value = true;
    error.value = null;

    log('usePayrollPeriods', 'getPeriodSummary', 'Fetching summary', { periodId, franchiseId });

    try {
      const response = await httpService.get(`/payroll/period_summary/${periodId}`, {
        params: { franchise_id: franchiseId },
        responseContract: 'Payroll_PeriodSummaryResponse'
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to get period summary');
      }

      const summary = response.dados;
      log('usePayrollPeriods', 'getPeriodSummary', 'Summary loaded', { periodId });

      return summary;
    } catch (err) {
      error.value = err.message || 'An unexpected error occurred';
      log('usePayrollPeriods', 'getPeriodSummary', 'ERROR', { error: err.message });
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  log('usePayrollPeriods', 'init', 'Composable initialized with centralized status management');

  return {
    // State
    periods,
    currentPeriod,
    isLoading,
    error,

    // Methods
    listPeriods,
    getPeriod,
    createPeriod,
    archivePeriod,
    deletePeriod,
    payPeriod,
    reversePeriod,
    getUndoPreview,
    undoPeriod,
    getPeriodSummary,
    getNextPeriodDates,

    // Status Management - Now from usePayrollStatus.js (Single Source of Truth)
    // These are re-exported for backward compatibility
    PERIOD_STATUS,
    PERIOD_STATUS_LABELS,
    periodStatusStore,     // Reactive store - use for computed properties
    getPeriodStatus,       // Get current status for a period
    isStatusAtLeast,       // Check if period has reached a status level
    updatePeriodStatus,    // ONLY use when processing API responses in other composables
    syncStatusFromPeriodData,  // Helper for syncing from period objects

    // New centralized functions from usePayrollStatus.js
    PAYROLL_STATUS,        // Preferred name for status constants
    PAYROLL_STATUS_LABELS, // Preferred name for status labels
    getStatusLabel,        // Get human-readable status label
    getWizardState         // Get complete wizard state from period data
  };
}
